/**
 * A job's profit and loss from QuickBooks (the P&L tab on a job): QuickBooks's "Profit and Loss
 * Detail" report for the job, for its whole life (accrual), turned into income, job materials,
 * labor (every other expense), overhead (a share of income), net profit, and commission below it.
 */

export interface QbPnlLine {
  date: string;
  /** "Invoice", "Bill", "Expense", "Check"... */
  type: string;
  /** QuickBooks's transaction id, when it gives one. */
  txnId: string | null;
  num: string;
  name: string;
  memo: string;
  account: string;
  amountCents: number;
}

export interface QbPnlGroup {
  lines: QbPnlLine[];
  totalCents: number;
}

export interface QbJobPnl {
  income: QbPnlGroup;
  materials: QbPnlGroup;
  labor: QbPnlGroup;
  overheadPercent: number;
  overheadCents: number;
  /** Materials + labor + overhead. */
  expensesCents: number;
  netCents: number;
  /** Net as a share of income, one decimal; null with no income. */
  marginPercent: number | null;
  /** Commission (the commission accounts): figured on net profit, so not in it; shown below. */
  commission: QbPnlGroup;
  /** Net profit less commission. */
  afterCommissionCents: number;
  /** The job's sales rep's default commission on its net profit (none on a loss); null without a rep or a rate. */
  expectedCommission: { rep: string; percent: number; cents: number } | null;
  /** What the job's material orders come to at catalog prices (not cancelled); an estimate, not in the totals. */
  estimatedMaterials?: { cents: number; orders: number; unpriced: number } | null;
  /** The QuickBooks name of the job the report is for. */
  qbName: string;
  runAt: string;
  /** The first and last transaction dates. */
  period: { start: string | null; end: string | null };
}

/** A line with its group (Income, COGS, Expenses...), its account, and (in the report) the accounts it's under. */
type PnlEntry = { group: string; accountId: string | null; line: QbPnlLine; parents?: { id: string | null; name: string }[] };

/** The parts of QuickBooks's report JSON that are read. */
interface QbCol {
  value?: string;
  id?: string;
}
interface QbRow {
  type?: string;
  group?: string;
  Header?: { ColData?: QbCol[] };
  Rows?: { Row?: QbRow[] };
  ColData?: QbCol[];
  Summary?: { ColData?: QbCol[] };
}
export interface QbPnlReport {
  Header?: { StartPeriod?: string; EndPeriod?: string; ReportBasis?: string; Customer?: string; Option?: { Name?: string; Value?: string }[] };
  Columns?: { Column?: { ColTitle?: string; ColType?: string; MetaData?: { Name: string; Value: string }[] }[] };
  Rows?: { Row?: QbRow[] };
}

/** A commission on net profit, in hundredths of a percent (40% = 4000); nothing on a loss. Rounded to the cent. */
export function commissionCents(netCents: number, bps: number): number {
  return netCents > 0 ? Math.round((netCents * bps) / 10_000) : 0;
}

/** "1,234.56" or "-80" → cents. */
export function qbCents(v: string | undefined): number {
  const n = Number(String(v ?? "").replace(/[,$\s]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

const INCOME = new Set(["Income", "OtherIncome"]);
const EXPENSE = new Set(["COGS", "Expenses", "OtherExpenses"]);

/** Column titles, for a report whose columns carry no keys. */
const TITLE_KEYS: Record<string, string> = { date: "tx_date", "transaction date": "tx_date", "transaction type": "txn_type", num: "doc_num", "no.": "doc_num", name: "name", "memo/description": "memo", memo: "memo", amount: "subt_nat_amount" };

/**
 * A section's group: QuickBooks's code for it, or else its heading ("Income", "Cost of Goods Sold",
 * "Expenses", "Other Income", "Other Expenses"). Wrappers like "Ordinary Income/Expenses" aren't one.
 */
const HEADINGS: Record<string, string> = { income: "Income", "other income": "OtherIncome", "cost of goods sold": "COGS", cogs: "COGS", expenses: "Expenses", expense: "Expenses", "other expenses": "OtherExpenses", "other expense": "OtherExpenses" };
function sectionGroup(code: string | undefined, heading: string | undefined): string {
  return code || HEADINGS[(heading ?? "").trim().toLowerCase()] || "";
}

/** Every transaction line in the report, with its top-level group (Income, COGS, Expenses...) and account. */
export function qbPnlLines(report: QbPnlReport): PnlEntry[] {
  // Columns by their key; a report without keys is read by the columns' titles.
  const cols = (report.Columns?.Column ?? []).map((c) => c.MetaData?.find((m) => m.Name === "ColKey")?.Value ?? TITLE_KEYS[(c.ColTitle ?? "").trim().toLowerCase()] ?? c.ColType ?? "");
  const at = (key: string) => cols.indexOf(key);
  const amountAt = [at("subt_nat_amount"), at("subt_nat_home_amount"), at("nat_amount"), cols.findIndex((c) => c === "Money")].find((i) => i >= 0) ?? -1;
  const out: PnlEntry[] = [];
  // Sections nest: wrappers ("Ordinary Income/Expenses"), then the group ("Income"), then accounts
  // and their sub-accounts; transaction lines sit in the innermost one.
  const walk = (rows: QbRow[], group: string, accounts: { id: string | null; name: string }[]) => {
    for (const r of rows) {
      if (r.ColData && !r.Rows?.Row && (!r.type || r.type.toLowerCase() === "data")) {
        const c = (i: number) => (i >= 0 ? (r.ColData![i]?.value ?? "") : "");
        const typeCol = at("txn_type");
        const acct = accounts[accounts.length - 1];
        out.push({
          group,
          accountId: [...accounts].reverse().find((a) => a.id)?.id ?? null,
          parents: accounts,
          line: { date: c(at("tx_date")), type: c(typeCol), txnId: typeCol >= 0 ? (r.ColData[typeCol]?.id ?? null) : null, num: c(at("doc_num")), name: c(at("name")), memo: c(at("memo")), account: acct?.name ?? "", amountCents: qbCents(c(amountAt)) },
        });
      } else if (r.Rows?.Row) {
        const head = r.Header?.ColData?.[0];
        if (!group) walk(r.Rows.Row, sectionGroup(r.group, head?.value), []);
        else walk(r.Rows.Row, group, head?.value ? [...accounts, { id: head.id ?? null, name: head.value }] : accounts);
      }
    }
  };
  walk(report.Rows?.Row ?? [], "", []);
  return out;
}

export function jobPnl(report: QbPnlReport, opts: PnlOptions): QbJobPnl {
  return { ...pnlOf(qbPnlLines(report), opts), period: { start: report.Header?.StartPeriod ?? null, end: report.Header?.EndPeriod ?? null } };
}

const groupOf = (lines: QbPnlLine[]): QbPnlGroup => ({ lines, totalCents: lines.reduce((s, l) => s + l.amountCents, 0) });

export interface PnlOptions {
  materialAccountIds: string[];
  /** Commission accounts: kept out of net profit (it's figured on net profit), shown below it. */
  commissionAccountIds?: string[];
  overheadPercent: number;
  qbName: string;
  runAt: string;
  /** The job's sales rep and their default commission (hundredths of a percent). */
  salesRep?: { name: string; commissionBps: number | null } | null;
}

/**
 * Income; then expenses split into job materials (the chosen accounts, or any account named
 * "...materials..." when none are chosen) and labor (everything else); overhead at a share of
 * income; and net profit.
 */
function pnlOf(all: PnlEntry[], opts: PnlOptions): QbJobPnl {
  // A sub-account counts when its parent account is picked (or named) too.
  const accountsOf = (x: PnlEntry) => (x.parents?.length ? x.parents : [{ id: x.accountId, name: x.line.account }]);
  const picks = (ids: string[], named: RegExp) => (x: PnlEntry) => accountsOf(x).some((a) => (ids.length ? !!a.id && ids.includes(a.id) : named.test(a.name)));
  const isMaterials = picks(opts.materialAccountIds, /material/i);
  const commissionIds = opts.commissionAccountIds ?? [];
  const isCommission = picks(commissionIds, /admin\s*sub-?contract/i);
  const income = groupOf(all.filter((x) => INCOME.has(x.group)).map((x) => x.line));
  const costs = all.filter((x) => EXPENSE.has(x.group));
  const commission = groupOf(costs.filter((x) => isCommission(x)).map((x) => x.line));
  const expenses = costs.filter((x) => !isCommission(x));
  const materials = groupOf(expenses.filter((x) => isMaterials(x)).map((x) => x.line));
  const labor = groupOf(expenses.filter((x) => !isMaterials(x)).map((x) => x.line));
  const overheadCents = Math.round((income.totalCents * opts.overheadPercent) / 100);
  const expensesCents = materials.totalCents + labor.totalCents + overheadCents;
  const netCents = income.totalCents - expensesCents;
  return {
    income,
    materials,
    labor,
    overheadPercent: opts.overheadPercent,
    overheadCents,
    expensesCents,
    netCents,
    marginPercent: income.totalCents ? Math.round((netCents / income.totalCents) * 1000) / 10 : null,
    commission,
    afterCommissionCents: netCents - commission.totalCents,
    expectedCommission: opts.salesRep && opts.salesRep.commissionBps !== null ? { rep: opts.salesRep.name, percent: opts.salesRep.commissionBps / 100, cents: commissionCents(netCents, opts.salesRep.commissionBps) } : null,
    qbName: opts.qbName,
    runAt: opts.runAt,
    period: { start: null, end: null },
  };
}

/** The job's commission bill status, for the P&L tab's button. */
export interface CommissionBillInfo {
  /** The job's commissioned rep, and the vendor contact they're paid as (null: not linked yet). */
  rep: { id: string; name: string; commissionBps: number | null; vendorContact: { id: string; name: string } | null; canText: boolean; canEmail: boolean } | null;
  /** The expense category for commissions (mapped to a commission account in QuickBooks); null: none set up. */
  category: string | null;
  /** Commission bills already made on this job (open and approved). */
  bills: { id: string; status: string; totalCents: number; createdAt: string }[];
  /** "Job #1043 Pat Smith", for the message to the rep. */
  jobLabel: string;
}

/**
 * What the rep is told about a commission bill (text or email): the job, net profit, the percentage and
 * the commission; when some was billed before, this bill and that. Nothing else from the P&L.
 */
export function commissionMessage(m: { job: string; netCents: number; percentBps: number; billCents: number; billedBeforeCents: number }): string {
  const usd = (c: number) => `$${(c / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const pct = `${(m.percentBps / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
  const total = commissionCents(m.netCents, m.percentBps);
  const head = `NMT: Commission for ${m.job}. Net profit ${usd(m.netCents)} × ${pct} = ${usd(total)}.`;
  return m.billedBeforeCents > 0 || m.billCents !== total ? `${head} This bill: ${usd(m.billCents)}${m.billedBeforeCents > 0 ? ` (${usd(m.billedBeforeCents)} billed before)` : ""}.` : head;
}
